import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings } from 'lucide-react';
import { Button } from './ui/button';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after 1 second delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
        applyConsent(saved);
      } catch (e) {
        console.error('Error loading cookie consent:', e);
      }
    }
  }, []);

  const applyConsent = (prefs) => {
    // Apply Google Analytics
    if (prefs.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    } else if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }

    // Apply Facebook Pixel
    if (prefs.marketing && window.fbq) {
      window.fbq('consent', 'grant');
    } else if (window.fbq) {
      window.fbq('consent', 'revoke');
    }
  };

  const saveConsent = (prefs) => {
    localStorage.setItem('cookie_consent', JSON.stringify(prefs));
    localStorage.setItem('cookie_consent_timestamp', new Date().toISOString());
    applyConsent(prefs);
  };

  const acceptAll = () => {
    console.log('Accept All clicked');
    const prefs = {
      essential: true,
      analytics: true,
      marketing: true
    };
    setPreferences(prefs);
    saveConsent(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptEssential = () => {
    console.log('Accept Essential clicked');
    const prefs = {
      essential: true,
      analytics: false,
      marketing: false
    };
    setPreferences(prefs);
    saveConsent(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const saveCustom = () => {
    console.log('Save Custom clicked');
    saveConsent(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 flex items-end justify-center pointer-events-none" style={{ zIndex: 9999 }}>
      {/* Backdrop - Only show on desktop when settings are open */}
      {showSettings && (
        <div 
          className="hidden md:block absolute inset-0 bg-black/50 pointer-events-auto"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Banner */}
      <div 
        className="relative w-full md:max-w-5xl mb-0 md:mb-6 mx-0 md:mx-4 pointer-events-auto md:rounded-2xl border-t-2 md:border-2 border-rose-200"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15), 0 20px 60px rgba(0, 0, 0, 0.3)',
          zIndex: 10000
        }}
      >
        {!showSettings ? (
          // Simple Banner - Compact on mobile
          <div className="p-3 md:p-6">
            <div className="flex items-start gap-2 md:gap-4">
              <div className="p-2 md:p-3 bg-gradient-to-br from-rose-400 to-rose-600 rounded-xl flex-shrink-0">
                <Cookie className="w-4 h-4 md:w-6 md:h-6 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-1 md:mb-2">
                  🍪 We Use Cookies
                </h3>
                <p className="text-xs md:text-base text-gray-700 mb-2 md:mb-4 leading-relaxed line-clamp-2 md:line-clamp-none">
                  We use cookies to enhance your experience. Essential cookies are required. 
                  <span className="hidden md:inline">
                    {" "}Analytics (Google Analytics) and marketing cookies (Facebook Pixel) are optional and help us improve our service.
                  </span>
                </p>
                <p className="hidden md:block text-sm text-gray-600 mb-4">
                  By clicking "Accept All", you consent to our use of cookies. 
                  <a href="/privacy-policy" target="_blank" className="text-rose-600 hover:underline ml-1">
                    Learn more in our Privacy Policy
                  </a>
                </p>
                
                <div className="flex flex-wrap gap-2 md:gap-3">
                  <button
                    onClick={acceptAll}
                    className="px-3 py-1.5 md:px-6 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-medium bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white transition-all duration-200 shadow-lg hover:shadow-xl"
                    data-testid="cookie-accept-all"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={acceptEssential}
                    className="px-3 py-1.5 md:px-6 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-medium border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200"
                    data-testid="cookie-essential-only"
                  >
                    Essential Only
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="px-3 py-1.5 md:px-6 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-medium border-2 border-rose-300 text-rose-600 hover:bg-rose-50 transition-all duration-200 flex items-center gap-1 md:gap-2"
                    data-testid="cookie-settings"
                  >
                    <Settings className="w-3 h-3 md:w-4 md:h-4" />
                    <span className="hidden md:inline">Customize</span>
                    <span className="md:hidden">More</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Settings Modal - Full screen on mobile, modal on desktop
          <div className="p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4 md:mb-6">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-2 md:p-3 bg-gradient-to-br from-rose-400 to-rose-600 rounded-xl">
                  <Settings className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900">Cookie Settings</h3>
                  <p className="text-xs md:text-sm text-gray-600">Manage your preferences</p>
                </div>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
              {/* Essential Cookies */}
              <div className="flex items-start justify-between p-3 md:p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex-1 mr-3 md:mr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm md:text-base font-semibold text-gray-900">Essential Cookies</h4>
                    <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs font-medium rounded-full">
                      Always Active
                    </span>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600">
                    Required for login, authentication, and core functionality.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="w-5 h-5 text-rose-500 border-gray-300 rounded cursor-not-allowed opacity-50"
                  />
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between p-3 md:p-4 bg-white rounded-xl border-2 border-rose-200">
                <div className="flex-1 mr-3 md:mr-4">
                  <h4 className="text-sm md:text-base font-semibold text-gray-900 mb-1">Analytics Cookies</h4>
                  <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-2">
                    Help us understand how visitors use our site.
                  </p>
                  <p className="text-xs text-gray-500">
                    Google Analytics - anonymized data
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="sr-only peer"
                      data-testid="analytics-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-rose-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-600"></div>
                  </label>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start justify-between p-3 md:p-4 bg-white rounded-xl border-2 border-rose-200">
                <div className="flex-1 mr-3 md:mr-4">
                  <h4 className="text-sm md:text-base font-semibold text-gray-900 mb-1">Marketing Cookies</h4>
                  <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-2">
                    Track ad effectiveness and show relevant ads.
                  </p>
                  <p className="text-xs text-gray-500">
                    Facebook Pixel - conversion tracking
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                      className="sr-only peer"
                      data-testid="marketing-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-rose-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-600"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-2 md:gap-3 pt-4 border-t">
              <button
                onClick={saveCustom}
                className="flex-1 px-4 md:px-6 py-2.5 md:py-3 rounded-xl text-sm md:text-base font-medium bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white transition-all duration-200 shadow-lg"
                data-testid="cookie-save-preferences"
              >
                Save My Preferences
              </button>
              <button
                onClick={acceptAll}
                className="px-4 md:px-6 py-2.5 md:py-3 rounded-xl text-sm md:text-base font-medium border-2 border-rose-300 text-rose-600 hover:bg-rose-50 transition-all duration-200"
              >
                Accept All
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-3 md:mt-4 text-center">
              You can change these settings anytime.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
